import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useSearchParams,
} from 'react-router-dom';
import { PrimeReactProvider, addLocale, locale } from 'primereact/api';
import 'primereact/resources/themes/mira/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './index.css';
import { AuthProvider } from './contexts/AuthContext';
import { getToken } from './services/Token';
import Login from './pages/Login';
import Principal from './pages/Principal';
import Dashboard from './pages/Dashboard';
import AgentesPage from './pages/AgentesPage';
import PerfilPage from './pages/PerfilPage';
import ConfiguracoesPage from './pages/ConfiguracoesPage';
import NfseEmissorPage from './pages/nfse/NfseEmissorPage';
import NfseEmissaoPage from './pages/nfse/NfseEmissaoPage';
import NfseTomadasPage from './pages/nfse/NfseTomadasPage';
import NfseAjustePage from './pages/nfse/NfseAjustePage';
import HelpPage from './pages/HelpPage';
import FinanceiroPage from './pages/FinanceiroPage';

addLocale('pt', {
  accept: 'Sim',
  reject: 'Não',
  choose: 'Escolher',
  upload: 'Enviar',
  cancel: 'Cancelar',
  dayNames: ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'],
  dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
  dayNamesMin: ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'],
  monthNames: [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
  ],
  monthNamesShort: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
  today: 'Hoje',
  clear: 'Limpar',
  weekHeader: 'Sm',
  firstDayOfWeek: 0,
  dateFormat: 'dd/mm/yy',
  weak: 'Fraco',
  medium: 'Médio',
  strong: 'Forte',
  passwordPrompt: 'Digite uma senha',
  emptyFilterMessage: 'Nenhum resultado encontrado',
  emptyMessage: 'Nenhuma opção disponível',
  startsWith: 'Começa com',
  contains: 'Contém',
  notContains: 'Não contém',
  endsWith: 'Termina com',
  equals: 'Igual',
  notEquals: 'Diferente',
  noFilter: 'Sem filtro',
  lt: 'Menor que',
  lte: 'Menor ou igual a',
  gt: 'Maior que',
  gte: 'Maior ou igual a',
  dateIs: 'Data é',
  dateIsNot: 'Data não é',
  dateBefore: 'Data antes de',
  dateAfter: 'Data depois de',
  matchAll: 'Corresponder todos',
  matchAny: 'Corresponder qualquer',
  addRule: 'Adicionar regra',
  removeRule: 'Remover regra',
  apply: 'Aplicar',
  selectionMessage: '{0} itens selecionados',
  emptySelectionMessage: 'Nenhum item selecionado',
  emptySearchMessage: 'Nenhum resultado encontrado',
});
locale('pt');

/**
 * Gate reativo: reavalia o token a cada navegação/render.
 */
function RequireAuth() {
  const token = (getToken() || '').trim();
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return <Outlet />;
}

function LoginRoute() {
  const token = (getToken() || '').trim();
  if (token) {
    return <Navigate to="/" replace />;
  }
  return <Login />;
}

/** Ajuda ainda em preview/WIP — oculta do menu e só acessível via ?preview=1. */
function HelpRoute() {
  const [searchParams] = useSearchParams();
  if (searchParams.get('preview') !== '1') {
    return <Navigate to="/" replace />;
  }
  return <HelpPage />;
}

const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginRoute />,
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <Principal />,
        children: [
          { path: '/', element: <Dashboard /> },
          { path: '/nfse/emissor', element: <NfseEmissorPage /> },
          { path: '/nfse/emissor/nova', element: <NfseEmissaoPage /> },
          { path: '/nfse/tomadas', element: <NfseTomadasPage /> },
          { path: '/nfse/ajuste', element: <NfseAjustePage /> },
          { path: '/nfse/emitir', element: <Navigate to="/nfse/emissor/nova" replace /> },
          { path: '/nfse/emitidas', element: <Navigate to="/nfse/emissor" replace /> },
          { path: '/agentes', element: <AgentesPage /> },
          { path: '/ajuda', element: <HelpRoute /> },
          { path: '/financeiro', element: <FinanceiroPage /> },
          { path: '/bi', element: <Navigate to="/configuracoes?tab=bi" replace /> },
          { path: '/perfil', element: <PerfilPage /> },
          { path: '/configuracoes', element: <ConfiguracoesPage /> },
          { path: '/empresa', element: <Navigate to="/perfil?tab=empresa" replace /> },
          { path: '/empresa/filiais', element: <Navigate to="/perfil?tab=empresa" replace /> },
          { path: '/empresa/certificado', element: <Navigate to="/perfil?tab=empresa" replace /> },
          { path: '/caracteristicas-nfse', element: <Navigate to="/configuracoes" replace /> },
          { path: '/modelos-texto', element: <Navigate to="/configuracoes?tab=modelos" replace /> },
          { path: '/contas-bancarias', element: <Navigate to="/configuracoes?tab=contas" replace /> },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);

const primeReactConfig = {
  hideOverlaysOnDocumentScrolling: true,
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <PrimeReactProvider value={primeReactConfig}>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </PrimeReactProvider>
  </React.StrictMode>
);
